import { useState, useEffect, useRef, Fragment } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../utils/api";
import { useAuth } from "../context/AuthContext";
import DashboardHeader from "../components/DashboardHeader";
import { useReactToPrint } from "react-to-print";
import { downloadPDF, formatDuration, fetchDoctorFilter } from "../utils/reportUtils";

export default function StaffDetailsReport() {
  const navigate = useNavigate();
  const { logout, currentUser, userRole } = useAuth();
  const printRef = useRef();
  const [isProcessing, setIsProcessing] = useState(false);
  const [searchParams] = useSearchParams();

  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [profileFields, setProfileFields] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [reportType, setReportType] = useState(searchParams.get("type") || "dci"); 
  const [letterhead, setLetterhead] = useState({
    collegeName: "ADHIPARASAKTHI DENTAL COLLEGE & HOSPITAL",
    address: "MELMARUVATHUR, TAMIL NADU - 603319",
    dciTitle: "DCI REPORT",
    tnmgrTitle: "Staff Details for TNMGR University",
    phone: "",
    email: "",
  });

  const [filterDept, setFilterDept] = useState("");
  const [filterDesig, setFilterDesig] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => { 
    const typeUrl = searchParams.get("type");
    if (typeUrl) setReportType(typeUrl);
  }, [searchParams]);

  useEffect(() => { fetchData(); }, []);

  async function fetchData() {
    setLoading(true);
    try {
      // Run independent queries concurrently to prevent 13-15 sec UI freeze
      const mapRows = (res) =>
        Array.isArray(res)
          ? res.map((row) => ({
              id: row.id,
              ...(typeof row.data === "string" ? JSON.parse(row.data) : row.data || row),
            }))
          : [];

      const [usersRes, isDoctorUser, ffRes, desRes, lhRes, dRes] = await Promise.all([
        api.get("/users"),
        fetchDoctorFilter(),
        api.get("/faculty_profile_fields?orderBy=order"),
        api.get("/designations?orderBy=name"),
        api.get("/settings/letterhead").catch(() => null),
        api.get("/departments?orderBy=name"),
      ]);

      const allUsers = mapRows(usersRes);
      const loggedInUser = allUsers.find(u => u.id === currentUser.uid);
      const isFacultyOnly = userRole === "faculty";
      const facultyDept = loggedInUser?.department;

      let list = allUsers
        .filter(u =>
          (u.role === "faculty" || u.role === "admin") &&
          u.status !== "inactive" &&
          isDoctorUser(u)
        )
        .sort((a, b) => (a.name || "").localeCompare(b.name || ""));

      if (isFacultyOnly && facultyDept) {
        list = list.filter(u => u.department === facultyDept);
      }

      setStaffList(list);
      setProfileFields(mapRows(ffRes));

      if (isFacultyOnly && facultyDept) {
        setDepartments([facultyDept]);
        setFilterDept(facultyDept); 
      } else {
        setDepartments(mapRows(dRes).map((d) => d.name).filter(Boolean));
      }

      setDesignations(mapRows(desRes).map((d) => d.name).filter(Boolean));
      if (lhRes) setLetterhead((prev) => ({ ...prev, ...(lhRes.data || lhRes) }));

    } catch (err) { console.error(err); }
    setLoading(false);
  }

  async function handleLogout() { 
    await logout(); 
    navigate(userRole === "admin" || userRole === "superadmin" ? "/admin" : "/login"); 
  }

  const getFieldKey = (f) => f.key || f.name?.toLowerCase().replace(/[^a-z0-9]/g, "_");

  const filteredStaff = staffList.filter(s => {
    const matchDept = !filterDept || (s.department || "").toLowerCase().trim() === filterDept.toLowerCase().trim();
    const matchDesig = !filterDesig || (s.designation || "").toLowerCase().trim() === filterDesig.toLowerCase().trim();
    const searchLow = searchTerm.toLowerCase();
    const matchSearch = !searchTerm ||
      s.name?.toLowerCase().includes(searchLow) ||
      s.email?.toLowerCase().includes(searchLow) ||
      s.empId?.toLowerCase().includes(searchLow);
    return matchDept && matchDesig && matchSearch;
  });

  function getDeptGroups() {
    const groups = {};
    filteredStaff.forEach(s => {
      const dept = normalizeTitleCase(s.department);
      if (!groups[dept]) groups[dept] = [];
      groups[dept].push(s);
    });
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }

  const getJoinDate = (staff, profileFields) => {
    const commonKeys = ["dateOfJoining", "date_of_joining", "joiningDate", "joining_date", "doj"];
    for (const k of commonKeys) {
      if (staff[k]) return staff[k];
    }
    const found = profileFields?.find(f => {
      const nm = (f.name || "").toLowerCase();
      return nm.includes("joining") || nm.includes("doj");
    });
    if (found) {
      const k = getFieldKey(found);
      return staff[k];
    }
    return null;
  };

  const getDOB = (staff, profileFields) => {
    const commonKeys = ["dateOfBirth", "date_of_birth", "dob", "birthDate"];
    for (const k of commonKeys) {
      if (staff[k]) return staff[k];
    }
    const found = profileFields?.find(f => {
      const nm = (f.name || "").toLowerCase();
      return nm.includes("birth") || nm === "dob";
    });
    if (found) {
      const k = getFieldKey(found);
      return staff[k];
    }
    return null;
  };

  const formatDate = (dateStr, isToDate = false) => {
    if (!dateStr || dateStr.toLowerCase() === "till date") return isToDate ? "Till Date" : (dateStr || "—");
    if (!dateStr.includes("-")) return dateStr;
    const parts = dateStr.split("-");
    if (parts[0].length === 4) return `${parts[2]}-${parts[1]}-${parts[0]}`;
    return dateStr;
  };

  function calcExp(from, to) {
    if (!from) return "";
    let start = from.includes("-") && from.split("-")[0].length === 4 ? new Date(from) : new Date(from.split("-").reverse().join("-"));
    let end = (!to || to.toLowerCase() === "till date") ? new Date() : (to.includes("-") && to.split("-")[0].length === 4 ? new Date(to) : new Date((to || "").split("-").reverse().join("-")));
    if (isNaN(start) || isNaN(end) || end < start) return "—";
    let years = end.getFullYear() - start.getFullYear();
    let months = end.getMonth() - start.getMonth();
    let days = end.getDate() - start.getDate();
    if (days < 0) { months -= 1; const prevMonth = new Date(end.getFullYear(), end.getMonth(), 0); days += prevMonth.getDate(); }
    if (months < 0) { years -= 1; months += 12; }
    const parts = [];
    if (years > 0) parts.push(`${years} Year${years === 1 ? "" : "s"}`);
    if (months > 0) parts.push(`${months} Month${months === 1 ? "" : "s"}`);
    if (days > 0) parts.push(`${days} Day${days === 1 ? "" : "s"}`);
    return parts.length > 0 ? parts.join(" ") : "0 Days";
  }

  const formatTotalExp = (totalDays) => {
    if (!totalDays || totalDays <= 0) return "—";
    const expY = Math.floor(totalDays / 365);
    const rem = totalDays % 365;
    const expM = Math.floor(rem / 30);
    const expD = rem % 30;
    const parts = [];
    if (expY > 0) parts.push(`${expY} Year${expY === 1 ? "" : "s"}`);
    if (expM > 0) parts.push(`${expM} Month${expM === 1 ? "" : "s"}`);
    if (expD > 0) parts.push(`${expD} Day${expD === 1 ? "" : "s"}`);
    return parts.join(" ");
  };

  function handleExportCSV() {
    let headers = [];
    let rows = [];
    if (reportType === "dci") {
      headers = ["S.No.", "S.ID", "Name", "Designation", "DOB", "Qual_Degree", "Qual_YOP", "Qual_Univ", "Exp_Desig", "Exp_Place", "Exp_From", "Exp_To", "Exp_Duration"];
      let sno = 1;
      getDeptGroups().forEach(([dept, staffs]) => {
        rows.push([`--- ${dept} ---`]);
        staffs.forEach(s => {
          const quals = s.educationalQualifications || [];
          const instExps = (s.designationHistory || []).map(d => ({ jobTitle: d.designation || "Faculty", name: "APDCH", fromDate: d.fromDate, toDate: d.toDate || "Till Date" })).filter(d => d.fromDate);
          const exps = [...(s.workExperiences || []), ...instExps];
          const rowCount = Math.max(quals.length, exps.length, 1);
          for (let ri = 0; ri < rowCount; ri++) {
            const q = quals[ri]; const e = exps[ri];
            rows.push([ri === 0 ? sno : "", ri === 0 ? (s.empId || "") : "", ri === 0 ? getNameWithTitle(s, profileFields) : "", ri === 0 ? (s.designation || "") : "", ri === 0 ? formatDate(getDOB(s, profileFields)) : "", q ? `${q.degree || ""}` : "", q ? (q.yop || "") : "", q ? (q.board || "") : "", e ? (e.jobTitle || "") : "", e ? (e.name || "") : "", e ? formatDate(e.fromDate) : "", e ? formatDate(e.toDate, true) : "", e ? calcExp(e.fromDate, e.toDate) : ""]);
          }
          sno++;
        });
      });
    } else if (reportType === "faculty_list") {
      const otherFields = profileFields.filter(f => !["name", "designation", "department", "empid", "emp_id", "staffid", "staff_id", "dob", "dateofbirth", "date_of_birth", "title", "staffname", "staff_name"].includes((f.key || "").toLowerCase().replace(/[^a-z0-9]/g, "")));
      headers = ["S.NO.", "S.ID", "NAME", "DEPT", "DESIG", "DOB", ...otherFields.map(f => f.name.toUpperCase()), "TOTAL EXP"];
      let sno = 1;
      getDeptGroups().forEach(([dept, staffs]) => {
        rows.push([`--- ${dept} ---`]);
        staffs.forEach(s => {
          const jd = getJoinDate(s, profileFields);
          const inst = (s.designationHistory || []).map(d => ({ fromDate: d.fromDate, toDate: d.toDate || "Till Date" })).filter(d => d.fromDate);
          if (inst.length === 0 && jd) inst.push({ fromDate: jd, toDate: "Till Date" });
          const exps = [...(s.workExperiences || []), ...inst];
          let days = 0;
          exps.forEach(e => {
            const start = new Date(e.fromDate?.includes("-") && e.fromDate.split("-")[0].length === 4 ? e.fromDate : e.fromDate?.split("-").reverse().join("-"));
            const end = (!e.toDate || e.toDate.toLowerCase() === "till date") ? new Date() : (e.toDate.includes("-") && e.toDate.split("-")[0].length === 4 ? new Date(e.toDate) : new Date((e.toDate || "").split("-").reverse().join("-")));
            if (!isNaN(start) && !isNaN(end) && end >= start) days += Math.floor((end - start) / (1000 * 60 * 60 * 24)) + 1;
          });
          rows.push([sno++, s.empId || "", getNameWithTitle(s, profileFields), s.department || "", s.designation || "", formatDate(getDOB(s, profileFields)), ...otherFields.map(f => s[getFieldKey(f)] || ""), formatTotalExp(days)]);
        });
      });
    } else {
      headers = ["S.NO.", "DESIGNATION", "NAME", "DOB", "AFFIDAVIT", "DCI & AADHAR", "FORM 16", "PREV EXP", "DOJ", "PRESENT EXP", "EXP ON JUNE 15", "SIGN"];
      let sno = 1;
      getDeptGroups().forEach(([dept, staffs]) => {
        rows.push([`--- ${dept} ---`]);
        staffs.forEach(s => {
          const aadharF = profileFields.find(f => f.name.toLowerCase().includes("aadhar") || f.name.toLowerCase().includes("aadhaar"));
          const dciF = profileFields.find(f => f.name.toLowerCase().includes("dci"));
          let dciVal = dciF ? s[getFieldKey(dciF)] : "";
          let aadharVal = aadharF ? s[getFieldKey(aadharF)] : "";
          let combinedUid = [dciVal, aadharVal].filter(Boolean).join(" & ");
          rows.push([sno++, s.designation || "", getNameWithTitle(s, profileFields), formatDate(getDOB(s, profileFields)), s.affidavit_date || "", combinedUid, "—", "—", formatDate(getJoinDate(s, profileFields)), "—", "—", ""]);
        });
      });
    }
    const csv = headers.join(",") + "\n" + rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${reportType.toUpperCase()}_Report_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `${reportType.toUpperCase()}_Report_${new Date().toISOString().split("T")[0]}`,
  });

  const handleDownloadPDF = async () => {
    setIsProcessing(true);
    await downloadPDF(printRef, `${reportType.toUpperCase()}_Report_${new Date().toISOString().split("T")[0]}.pdf`, { orientation: "l" });
    setIsProcessing(false);
  };

  const reportTitle = reportType === "dci" ? (letterhead.dciTitle || "DCI REPORT") : 
                      reportType === "faculty_list" ? "FACULTY LIST REPORT" : 
                      (letterhead.tnmgrTitle || "TNMGR UNIVERSITY REPORT");

  return (
    <div style={{ minHeight: "100vh", background: "#f0f4ff", fontFamily: "Georgia, serif" }}>

      <DashboardHeader label="Staff Report">
        <button onClick={() => navigate(-1)} style={{ background: "rgba(255,255,255,0.15)", color: "#fff", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 10, padding: "8px 16px", fontWeight: 600, fontSize: 15, cursor: "pointer" }}>← Back</button>
        <button onClick={handleLogout} style={{ background: "rgba(255,255,255,0.15)", color: "#fff", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 10, padding: "8px 16px", fontWeight: 600, fontSize: 15, cursor: "pointer" }}>🚪 Logout</button>
      </DashboardHeader>

      <main style={{ padding: "24px 32px" }}>

        <div className="no-print" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
          <div>
            <h1 style={{ fontSize: 26, fontWeight: 900, color: "#0f2557", margin: 0 }}>{reportTitle}</h1>
            <p style={{ color: "#64748b", fontSize: 15, marginTop: 4 }}>{filteredStaff.length} members found</p>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <div style={{ display: "flex", background: "#e2e8f0", borderRadius: 10, padding: 3, gap: 2 }}>
              {[
                { key: "dci", label: "📋 DCI" }, 
                { key: "tnmgr", label: "🎓 TNMGR" },
                ...(userRole === "admin" || userRole === "superadmin" ? [{ key: "faculty_list", label: "👥 STAFF LIST" }] : [])
              ].map(rt => (
                <button key={rt.key} onClick={() => setReportType(rt.key)}
                  style={{ padding: "7px 18px", borderRadius: 8, border: "none", fontWeight: 700, fontSize: 14, cursor: "pointer",
                    background: reportType === rt.key ? "#fff" : "transparent",
                    color: reportType === rt.key ? "#0f2557" : "#64748b" }}>
                  {rt.label}
                </button>
              ))}
            </div>
            
            <button onClick={handleDownloadPDF} disabled={isProcessing}
              style={{ background: "#7c3aed", color: "#fff", border: "none", borderRadius: 10, padding: "9px 20px", fontWeight: 700, cursor: isProcessing ? "not-allowed" : "pointer", fontSize: 15 }}>
              {isProcessing ? "⏳..." : "📄 PDF"}
            </button>
            <button onClick={() => handlePrint()}
              style={{ background: "#2563eb", color: "#fff", border: "none", borderRadius: 10, padding: "9px 20px", fontWeight: 700, cursor: "pointer", fontSize: 15 }}>
              🖨️ Print
            </button>
            <button onClick={handleExportCSV}
              style={{ background: "#16a34a", color: "#fff", border: "none", borderRadius: 10, padding: "9px 20px", fontWeight: 700, cursor: "pointer", fontSize: 15 }}>
              📥 CSV
            </button>
          </div>
        </div>

        <div style={{ background: "#fff", borderRadius: 16, padding: "16px", marginBottom: 24, display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }} className="no-print">
          <div>
            <label style={labelSt}>🏢 DEPARTMENT</label>
            <select value={filterDept} onChange={e => setFilterDept(e.target.value)} style={selectSt} disabled={userRole === "faculty"}>
              <option value="">All Departments</option>
              {departments.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div>
            <label style={labelSt}>🎖️ DESIGNATION</label>
            <select value={filterDesig} onChange={e => setFilterDesig(e.target.value)} style={selectSt}>
              <option value="">All Designations</option>
              {designations.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div>
            <label style={labelSt}>🔍 SEARCH</label>
            <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Name, email..." style={selectSt} />
          </div>
        </div>

        <div ref={printRef} style={{ background: "#fff", borderRadius: 20, boxShadow: "0 4px 25px rgba(0,0,0,0.05)", padding: "20px", border: "1px solid #e2e8f0" }}>
          
          <div className="print-only" style={{ display: "none", borderBottom: "2px solid #0f2557", paddingBottom: 20, marginBottom: 20 }}>
             <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
                <img src="/apdch_logo.png" alt="Logo" style={{ height: 70 }} />
                <div>
                   <h1 style={{ color: "#0f2557", margin: 0, fontSize: 26, fontWeight: 900 }}>ADHIPARASAKTHI DENTAL COLLEGE & HOSPITAL</h1>
                   <p style={{ margin: "4px 0 0 0", color: "#64748b", fontSize: 13, fontWeight: 700 }}>{letterhead.address}</p>
                   <h2 style={{ color: "#2563eb", margin: "12px 0 0 0", fontSize: 20, fontWeight: 800 }}>{reportTitle}</h2>
                </div>
             </div>
          </div>

          {loading ? (
            <div style={{ padding: 60, textAlign: "center", color: "#94a3b8" }}>⏳ Loading report data...</div>
          ) : filteredStaff.length === 0 ? (
            <div style={{ padding: 60, textAlign: "center", color: "#94a3b8" }}>No staff found matching the criteria.</div>
          ) : reportType === "dci" ? (
            <DCIReport deptGroups={getDeptGroups()} profileFields={profileFields} calcExp={calcExp} getJoinDate={getJoinDate} getDOB={getDOB} formatDate={formatDate} formatTotalExp={formatTotalExp} />
          ) : reportType === "faculty_list" ? (
            <FacultyListReport staffList={filteredStaff} profileFields={profileFields} getFieldKey={getFieldKey} formatDate={formatDate} formatTotalExp={formatTotalExp} getDOB={getDOB} getJoinDate={getJoinDate} />
          ) : (
            <TNMGRReport staffList={filteredStaff} profileFields={profileFields} getJoinDate={getJoinDate} getDOB={getDOB} formatDate={formatDate} formatTotalExp={formatTotalExp} getFieldKey={getFieldKey} calcExp={calcExp} />
          )}
        </div>
      </main>

      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-only { display: block !important; }
          body { background: white !important; margin: 0; padding: 0; }
          main { padding: 0 !important; }
          table { width: 100% !important; border-collapse: collapse; border: 1.5px solid #000 !important; }
          th, td { border: 1.2px solid #000 !important; padding: 6px 8px !important; color: #000 !important; font-size: 10pt; }
          @page { margin: 1cm; size: A4 landscape; }
        }
      `}</style>
    </div>
  );
}

export const normalizeTitleCase = (str) => {
  if (!str) return "General";
  return str.trim().toLowerCase().split(/\s+/).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
};

function getNameWithTitle(staff, profileFields) {
  const titleKeys = ["title", "salutation", "prefix"];
  let title = "";
  for (const k of titleKeys) { if (staff[k]) { title = staff[k]; break; } }
  if (!title && profileFields) {
    const found = profileFields.find(f => {
      const nm = (f.name || "").toLowerCase();
      return nm === "title" || nm === "salutation" || nm === "prefix" || nm.includes("title");
    });
    if (found) { const k = found.key || found.name.toLowerCase().replace(/[^a-z0-9]/g, "_"); title = staff[k]; }
  }
  return title ? `${title} ${staff.name || ""}` : (staff.name || "—");
}

function DCIReport({ deptGroups, profileFields, calcExp, getJoinDate, getDOB, formatDate, formatTotalExp }) {
  let globalSno = 1;
  return (
    <table style={{ width: "100%", borderCollapse: "collapse" }}>
      <thead>
        <tr style={{ background: "#f8fafc" }}>
          <th rowSpan={2} style={th}>S.No</th>
          <th rowSpan={2} style={th}>S.ID</th>
          <th rowSpan={2} style={th}>Name</th>
          <th rowSpan={2} style={th}>Designation</th>
          <th rowSpan={2} style={th}>DOB</th>
          <th colSpan={3} style={th}>Qualification</th>
          <th colSpan={5} style={th}>Teaching Experience</th>
        </tr>
        <tr style={{ background: "#f8fafc" }}>
          <th style={th}>Degree</th><th style={th}>Year</th><th style={th}>Univ</th>
          <th style={th}>Designation</th><th style={th}>Place</th><th style={th}>From</th><th style={th}>To</th><th style={th}>Duration</th>
        </tr>
      </thead>
      <tbody>
        {deptGroups.map(([dept, staff]) => (
          <Fragment key={dept}>
            <tr><td colSpan={13} style={{ background: "#f1f5f9", fontWeight: 900, padding: "10px", border: "1.2px solid #000" }}>{dept}</td></tr>
            {staff.map(s => {
              const quals = s.educationalQualifications || [];
              const inst = (s.designationHistory || []).map(d => ({ jobTitle: d.designation, name: "APDCH", fromDate: d.fromDate, toDate: d.toDate || "Till Date" })).filter(d => d.fromDate);
              const exps = [...(s.workExperiences || []), ...inst];
              const rows = Math.max(quals.length, exps.length, 1);
              const sno = globalSno++;
              return Array.from({ length: rows }).map((_, i) => (
                <tr key={`${s.id}-${i}`}>
                  {i === 0 && (
                    <>
                      <td rowSpan={rows} style={{ ...td, textAlign: "center" }}>{sno}</td>
                      <td rowSpan={rows} style={td}>{s.empId || ""}</td>
                      <td rowSpan={rows} style={{ ...td, fontWeight: 700 }}>{getNameWithTitle(s, profileFields)}</td>
                      <td rowSpan={rows} style={td}>{s.designation || ""}</td>
                      <td rowSpan={rows} style={td}>{formatDate(getDOB(s, profileFields))}</td>
                    </>
                  )}
                  <td style={td}>{quals[i]?.degree || ""}</td>
                  <td style={td}>{quals[i]?.yop || ""}</td>
                  <td style={td}>{quals[i]?.board || ""}</td>
                  <td style={td}>{exps[i]?.jobTitle || ""}</td>
                  <td style={td}>{exps[i]?.name || ""}</td>
                  <td style={td}>{formatDate(exps[i]?.fromDate)}</td>
                  <td style={td}>{formatDate(exps[i]?.toDate, true)}</td>
                  <td style={td}>{exps[i] ? calcExp(exps[i].fromDate, exps[i].toDate) : ""}</td>
                </tr>
              ));
            })}
          </Fragment>
        ))}
      </tbody>
    </table>
  );
}

function FacultyListReport({ staffList, profileFields, getFieldKey, formatDate, formatTotalExp, getDOB, getJoinDate }) {
  const otherFields = profileFields.filter(f => !["name", "designation", "department", "empid", "emp_id", "staffid", "staff_id", "dob", "dateofbirth", "date_of_birth", "title", "staffname", "staff_name"].includes((f.key || "").toLowerCase().replace(/[^a-z0-9]/g, "")));
  let globalSno = 1;

  const groups = {};
  staffList.forEach(s => { const d = normalizeTitleCase(s.department); if (!groups[d]) groups[d] = []; groups[d].push(s); });
  const sortedDepts = Object.entries(groups).sort((a,b) => a[0].localeCompare(b[0]));

  return (
    <table style={{ width: "100%", borderCollapse: "collapse" }}>
      <thead>
        <tr style={{ background: "#f8fafc" }}>
          <th style={th}>S.No</th><th style={th}>S.ID</th><th style={th}>Name</th><th style={th}>Dept</th><th style={th}>Desig</th><th style={th}>DOB</th>
          {otherFields.map(f => <th key={f.id} style={th}>{f.name.toUpperCase()}</th>)}
          <th style={th}>Exp</th>
        </tr>
      </thead>
      <tbody>
        {sortedDepts.map(([dept, staffs]) => (
          <Fragment key={dept}>
            <tr><td colSpan={6 + otherFields.length + 1} style={{ background: "#f1f5f9", fontWeight: 900, padding: "10px", border: "1.2px solid #000" }}>{dept}</td></tr>
            {staffs.map(s => {
              const jd = getJoinDate(s, profileFields);
              const inst = (s.designationHistory || []).map(d => ({ fromDate: d.fromDate, toDate: d.toDate || "Till Date" })).filter(d => d.fromDate);
              if (inst.length === 0 && jd) inst.push({ fromDate: jd, toDate: "Till Date" });
              const exps = [...(s.workExperiences || []), ...inst];
              let days = 0;
              exps.forEach(e => {
                const start = new Date(e.fromDate?.includes("-") && e.fromDate.split("-")[0].length === 4 ? e.fromDate : e.fromDate?.split("-").reverse().join("-"));
                const end = (!e.toDate || e.toDate.toLowerCase() === "till date") ? new Date() : (e.toDate.includes("-") && e.toDate.split("-")[0].length === 4 ? new Date(e.toDate) : new Date((e.toDate || "").split("-").reverse().join("-")));
                if (!isNaN(start) && !isNaN(end) && end >= start) days += Math.floor((end - start) / (1000 * 60 * 60 * 24)) + 1;
              });
              return (
                <tr key={s.id}>
                  <td style={{ ...td, textAlign: "center" }}>{globalSno++}</td>
                  <td style={td}>{s.empId || ""}</td>
                  <td style={{ ...td, fontWeight: 700 }}>{getNameWithTitle(s, profileFields)}</td>
                  <td style={td}>{s.department || ""}</td>
                  <td style={td}>{s.designation || ""}</td>
                  <td style={td}>{formatDate(getDOB(s, profileFields))}</td>
                  {otherFields.map(f => {
                    const key = getFieldKey(f);
                    const val = s[key];
                    const isImage = (f.type || "").toLowerCase() === "image" || (val && typeof val === "string" && val.startsWith("http") && val.match(/\.(jpeg|jpg|gif|png|webp|svg)/i));
                    return (
                      <td key={f.id} style={td}>
                        {isImage && val ? (
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                             <img src={val} alt="Img" style={{ width: 30, height: 30, borderRadius: 4, objectFit: "cover", border: "1px solid #e2e8f0" }} />
                             <a href={val} target="_blank" rel="noreferrer" className="no-print" style={{ color: "#2563eb", textDecoration: "none", fontSize: 10 }}>View</a>
                          </div>
                        ) : (val || "")}
                      </td>
                    );
                  })}
                  <td style={{ ...td, fontWeight: 700 }}>{formatTotalExp(days)}</td>
                </tr>
              );
            })}
          </Fragment>
        ))}
      </tbody>
    </table>
  );
}

function TNMGRReport({ staffList, profileFields, getJoinDate, getDOB, formatDate, formatTotalExp, getFieldKey, calcExp }) {
  const targetYear = new Date().getFullYear();
  const targetDate = `${targetYear}-06-15`;
  let globalSno = 1;

  const groups = {};
  staffList.forEach(s => { const d = normalizeTitleCase(s.department); if (!groups[d]) groups[d] = []; groups[d].push(s); });
  const sortedDepts = Object.entries(groups).sort((a,b) => a[0].localeCompare(b[0]));

  const getExpOnJune15 = (staff) => {
    const jd = getJoinDate(staff, profileFields);
    const inst = (staff.designationHistory || []).map(d => ({ fromDate: d.fromDate, toDate: d.toDate || "Till Date" })).filter(d => d.fromDate);
    if (inst.length === 0 && jd) inst.push({ fromDate: jd, toDate: "Till Date" });
    const exps = [...(staff.workExperiences || []), ...inst];
    let days = 0;
    exps.forEach(e => {
      const s = e.fromDate?.includes("-") && e.fromDate.split("-")[0].length === 4 ? new Date(e.fromDate) : new Date(e.fromDate?.split("-").reverse().join("-"));
      let en = (!e.toDate || e.toDate.toLowerCase() === "till date") ? new Date(targetDate) : (e.toDate.includes("-") && e.toDate.split("-")[0].length === 4 ? new Date(e.toDate) : new Date(e.toDate?.split("-").reverse().join("-")));
      const t = new Date(targetDate);
      if (isNaN(s) || s > t) return;
      if (en > t) en = t;
      if (en >= s) days += Math.floor((en - s) / (1000 * 60 * 60 * 24)) + 1;
    });
    return formatTotalExp(days);
  };

  return (
    <table style={{ width: "100%", borderCollapse: "collapse" }}>
      <thead>
        <tr style={{ background: "#f8fafc" }}>
          <th style={th}>S.No</th><th style={th}>Desig</th><th style={th}>Name</th><th style={th}>DOB</th><th style={th}>Affidavit</th><th style={th}>DCI & Aadhar</th><th style={th}>Prev Exp</th><th style={th}>DOJ</th><th style={th}>Pres Exp</th><th style={th}>Exp On June 15</th><th style={th}>Sign</th>
        </tr>
      </thead>
      <tbody>
        {sortedDepts.map(([dept, staffs]) => (
          <Fragment key={dept}>
            <tr><td colSpan={11} style={{ background: "#f1f5f9", fontWeight: 900, padding: "10px", border: "1.2px solid #000" }}>{dept}</td></tr>
            {staffs.map(s => {
              const aadharF = profileFields.find(f => f.name.toLowerCase().includes("aadhar") || f.name.toLowerCase().includes("aadhaar"));
              const dciF = profileFields.find(f => f.name.toLowerCase().includes("dci"));
              let combinedUid = [dciF ? s[getFieldKey(dciF)] : null, aadharF ? s[getFieldKey(aadharF)] : null].filter(Boolean).join(" & ");
              return (
                <tr key={s.id}>
                  <td style={{ ...td, textAlign: "center" }}>{globalSno++}</td>
                  <td style={td}>{s.designation || ""}</td>
                  <td style={{ ...td, fontWeight: 700 }}>{getNameWithTitle(s, profileFields)}</td>
                  <td style={td}>{formatDate(getDOB(s, profileFields))}</td>
                  <td style={td}>{s.affidavit_date || "—"}</td>
                  <td style={td}>{combinedUid || "—"}</td>
                  <td style={td}>—</td>
                  <td style={td}>{formatDate(getJoinDate(s, profileFields))}</td>
                  <td style={td}>—</td>
                  <td style={{ ...td, fontWeight: 700 }}>{getExpOnJune15(s)}</td>
                  <td style={td}></td>
                </tr>
              );
            })}
          </Fragment>
        ))}
      </tbody>
    </table>
  );
}

const th = { padding: "10px", textAlign: "left", fontSize: "11px", fontWeight: 900, color: "#0f2557", border: "1.2px solid #000", background: "#f8fafc" };
const td = { padding: "8px", color: "#000", border: "1.2px solid #000", fontSize: "12px" };
const labelSt = { fontSize: 13, fontWeight: 900, color: "#475569", display: "block", marginBottom: 4 };
const selectSt = { width: "100%", background: "#fff", border: "1.5px solid #e2e8f0", borderRadius: 8, padding: "8px 12px", fontSize: 15, outline: "none" };
